import { Component, EventEmitter, Input, Output, TemplateRef } from "@angular/core";
import { Router } from "@angular/router";
import { NavigationEnum } from "src/app/enums/navigation.enum";
import { ScopeEnum } from "src/app/enums/scope.enum";
import { getCustomerIcon, getCustomerIconTooltip } from "src/app/utils/customer-icon.util";
import { openUrlIfAllowed } from "src/app/utils/redirect.util";
import { getSourceNumber, renderCitations, sortedSourceIndices } from "src/app/utils/source-citations.util";
type GenericRecord = any;

@Component({
  selector: "app-accordion-panel",
  standalone: false,
  templateUrl: "./accordion-panel.component.html",
  styleUrls: ["./accordion-panel.component.scss"]
})
export class AccordionPanelComponent {
  @Input() isOpen = false;
  @Output() isOpenChange = new EventEmitter<boolean>();

  @Input() isDisabled = false;
  @Input() showCheckbox = false;
  @Input() checked = false;
  @Output() checkedChange = new EventEmitter<boolean>();

  @Input() title = "";
  @Input() linkUrl: string | null = null;

  @Input() detailsMarkdown: string | null = null;
  /** A note shown above the text, e.g. the "previous results" disclaimer of a removed Warning result. */
  @Input() noticeText: string | null = null;
  @Input() relations: GenericRecord[] = [];
  @Input() relationTrackByKey = "_id";
  @Input() relationDetailsTab: "adverseMedia" | "sanctions" | "politicalInvolvements" | null = null;
  /** Opens a clicked relation in a new browser tab, so the current screen (e.g. a questionnaire in progress) stays put. */
  @Input() openRelationInNewTab = false;

  @Input() iconTemplate: TemplateRef<any> | null = null;
  @Input() titleTemplate: TemplateRef<any> | null = null;
  @Input() descriptionTemplate: TemplateRef<any> | null = null;
  @Input() relationsHeaderTemplate: TemplateRef<any> | null = null;
  @Input() relationsHeaderText: string | null = null;
  @Input() relationTemplate: TemplateRef<any> | null = null;
  @Input() contentTemplate: TemplateRef<any> | null = null;
  /** The matched list entry, shown above the text so a reviewer can read it before deciding. */
  @Input() matchTemplate: TemplateRef<any> | null = null;
  /** Shown instead of the notice and the text while they are being regenerated; the relations stay. */
  @Input() loadingTemplate: TemplateRef<any> | null = null;
  /** Rendered in the header, left of the chevron. Clicks there never toggle the panel. */
  @Input() actionTemplate: TemplateRef<any> | null = null;

  @Input() sources: GenericRecord[] = [];
  @Input() sourceTrackByKey = "_id";
  @Input() sourceLabelKey = "label";
  @Input() sourceUrlKey = "url";
  @Input() sourceCheckedKey = "checked";
  @Input() showSourcesChecklist = false;
  @Input() sourcesTitle = "add-document-slider.Include_Sources";
  @Input() renumberCheckedSources = true;
  @Input() disabledSourcesHint = "add-document-slider.Disabled_Sources_Hint";
  @Output() sourcesChange = new EventEmitter<any[]>();

  @Input() showCommentSection = false;
  @Input() commentOnlyWhenChecked = true;
  @Input() commentTitle = "add-document-slider.Include_Optional_Comment";
  @Input() commentPlaceholder = "add-document-slider.Add_Comment";
  @Input() commentMaxLength = 2000;
  @Input() commentText = "";
  @Output() commentTextChange = new EventEmitter<string>();
  @Input() disabledCommentHint = "add-document-slider.Disabled_Comment";
  public isCommentInputFocused = false;

  @Output() relationSelected = new EventEmitter<any>();

  constructor(private readonly router: Router) {}

  toggle(): void {
    if (this.isDisabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    this.isOpenChange.emit(this.isOpen);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggle();
    }
  }

  onMainCheckboxValueChange(nextChecked: boolean): void {
    this.checked = nextChecked;
    this.checkedChange.emit(nextChecked);
  }

  onRelationClick(relation: GenericRecord): void {
    this.relationSelected.emit(relation);
    if (this.relationDetailsTab && relation?._id) {
      const isArchived = !!relation?.isArchived;
      const baseRoute = isArchived ? ["/", "document", ScopeEnum.ARCHIVED, "customers"] : ["/", "document", NavigationEnum.CustomersList];
      const extras = isArchived ? {} : { queryParams: { tab: this.relationDetailsTab } };
      if (this.openRelationInNewTab) {
        openUrlIfAllowed(this.router.serializeUrl(this.router.createUrlTree([...baseRoute, relation._id], extras)));
        return;
      }
      this.router.navigate([...baseRoute, relation._id], extras);
    }
  }

  getRelationIcon(relation: GenericRecord): string {
    return getCustomerIcon(relation?.customerType, relation?.isDirectCustomer, relation?.isArchived);
  }

  getRelationTooltip(relation: GenericRecord): string {
    return getCustomerIconTooltip(relation?.isDirectCustomer, relation?.isArchived);
  }

  trackRelation = (index: number, relation: GenericRecord): string => String(relation?.[this.relationTrackByKey] ?? index);
  trackSource = (index: number, source: GenericRecord): string => String(source?.[this.sourceTrackByKey] ?? index);

  get checkedSourcesCount(): number {
    return this.sources.filter(source => !!source?.[this.sourceCheckedKey]).length;
  }

  get areAllSourcesChecked(): boolean {
    return Boolean(this.sources.length) && this.checkedSourcesCount === this.sources.length;
  }

  get shouldRenderSources(): boolean {
    return this.showSourcesChecklist && Boolean(this.sources.length);
  }

  get sortedSourceIndices(): number[] {
    return sortedSourceIndices(this.sources, this.sourceCheckedKey);
  }

  get isBottomSectionEnabled(): boolean {
    return !this.showCheckbox || this.checked;
  }

  get descriptionMarginClass(): string {
    return this.showCheckbox ? "ml-[76px]" : "ml-[46px]";
  }

  get shouldEnableCommentInput(): boolean {
    if (!this.showCommentSection) {
      return false;
    }
    if (!this.commentOnlyWhenChecked) {
      return true;
    }
    return this.checked;
  }

  get remainingCommentCharacters(): number {
    return Math.max(this.commentMaxLength - (this.commentText?.length ?? 0), 0);
  }

  getSourceNumber(index: number): number | "-" {
    return getSourceNumber(this.sources, index, this.sourceCheckedKey, this.renumberCheckedSources);
  }

  get renderedMarkdown(): string | null {
    return renderCitations(this.detailsMarkdown, this.sources, {
      urlKey: this.sourceUrlKey,
      checkedKey: this.sourceCheckedKey,
      renumberCheckedSources: this.renumberCheckedSources
    });
  }

  onSourceCheckboxValueChange(index: number, nextChecked: boolean): void {
    if (!this.isBottomSectionEnabled) {
      return;
    }
    const updatedSources = [...this.sources];
    updatedSources[index] = { ...updatedSources[index], [this.sourceCheckedKey]: nextChecked };
    this.sources = updatedSources;
    this.sourcesChange.emit(updatedSources);
  }

  onToggleAllSources(): void {
    if (!this.isBottomSectionEnabled) {
      return;
    }
    const nextChecked = !this.areAllSourcesChecked;
    const updatedSources = this.sources.map(source => ({ ...source, [this.sourceCheckedKey]: nextChecked }));
    this.sources = updatedSources;
    this.sourcesChange.emit(updatedSources);
  }

  onCommentChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.commentText = target.value;
    this.commentTextChange.emit(this.commentText);
  }

  onCommentFocus(): void {
    if (this.shouldEnableCommentInput) {
      this.isCommentInputFocused = true;
    }
  }

  onCommentBlur(): void {
    this.isCommentInputFocused = false;
  }

  onSourceLinkClick(event: Event): void {
    if (!this.isBottomSectionEnabled) {
      event.preventDefault();
    }
  }
}
